import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import * as admin from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BadgeCard } from "@/pages/settings/cards/BadgeCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { UserSummary } from "@/types/api";
import { useAuth } from "@/auth/useAuth";
import { usePortalConfig } from "@/portal/usePortalConfig";
import { extractErrorMessage } from "@/lib/api-errors";

type FormState = {
  username: string;
  password: string;
  is_admin: boolean;
  is_active: boolean;
  permissions: string[];
  one_c_user_ref: string;
  one_c_user_name: string;
};

const EMPTY: FormState = {
  username: "",
  password: "",
  is_admin: false,
  is_active: true,
  permissions: [],
  one_c_user_ref: "",
  one_c_user_name: "",
};

export function AdminUsersPage(): React.ReactElement {
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: admin.listUsers,
  });

  // Разделы инсталляции уже загружены до роутера (`/api/config`) — второй
  // запрос за тем же списком не нужен.
  const { pages, hasPage } = usePortalConfig();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const editing = editingId !== null;
  const isSelfEdit = editing && me?.id === editingId;

  // Fetch the user detail (with permissions) under React Query so we can gate
  // the form on `detailQuery.isFetching`. Without that, a fast admin can hit
  // «Сохранить» before the prefill resolves and submit body=EMPTY → demotes
  // and wipes permissions of the target user.
  const detailQuery = useQuery({
    queryKey: ["admin", "user-detail", editingId],
    queryFn: () => admin.getUser(editingId!),
    enabled: editing,
  });

  // Пользователи 1С для привязки автора вложений (тянем лениво при редактировании).
  const oneCUsersQuery = useQuery({
    queryKey: ["admin", "one-c-users"],
    queryFn: admin.listOneCUsers,
    enabled: editing,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const u = detailQuery.data;
    if (!u) return;
    setForm({
      username: u.username,
      password: "",
      is_admin: u.is_admin,
      is_active: u.is_active,
      permissions: [...u.permissions],
      one_c_user_ref: u.one_c_user_ref ?? "",
      one_c_user_name: u.one_c_user_name ?? "",
    });
  }, [detailQuery.data]);

  const onErr = (e: unknown) => toast.error(extractErrorMessage(e));

  const createMut = useMutation({
    mutationFn: admin.createUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setForm(EMPTY);
      toast.success("Пользователь создан");
    },
    onError: onErr,
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: number; body: admin.UserUpdateRequest }) =>
      admin.updateUser(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user-detail"] });
      setEditingId(null);
      setForm(EMPTY);
      toast.success("Сохранено");
    },
    onError: onErr,
  });

  const deleteMut = useMutation({
    mutationFn: admin.deleteUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("Пользователь деактивирован");
    },
    onError: onErr,
  });

  // Права SSO-связанного пользователя — роли Zitadel: здесь только просмотр
  // (бэкенд на их изменение отвечает 409 `sso_managed`).
  const ssoManaged = editing && detailQuery.data?.sso_managed === true;

  const mutating = createMut.isPending || updateMut.isPending;
  // Lock the form until prefill returns: prevents the race where Save fires
  // before useEffect re-seeds the fields.
  const prefilling = editing && detailQuery.isFetching && !detailQuery.data;
  const formLocked = mutating || prefilling;

  const togglePermission = (name: string) => {
    setForm((f) =>
      f.permissions.includes(name)
        ? { ...f, permissions: f.permissions.filter((p) => p !== name) }
        : { ...f, permissions: [...f.permissions, name] },
    );
  };

  const onToggleAdmin = (next: boolean) => {
    // Clear permissions when promoting to admin so the operator doesn't
    // unintentionally bring stale checkboxes back when toggling off later.
    setForm((f) => ({ ...f, is_admin: next, permissions: next ? [] : f.permissions }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      const body: admin.UserUpdateRequest = {
        is_active: form.is_active,
        one_c_user_ref: form.one_c_user_ref,
        one_c_user_name: form.one_c_user_name,
      };
      // Права SSO-пользователя не шлём вовсе: их источник — Zitadel.
      if (!ssoManaged) {
        body.is_admin = form.is_admin;
        body.permissions = form.permissions;
      }
      if (form.password) body.password = form.password;
      updateMut.mutate({ id: editingId!, body });
    } else {
      createMut.mutate({
        username: form.username,
        password: form.password,
        is_admin: form.is_admin,
        permissions: form.permissions,
      });
    }
  };

  const startEdit = (u: UserSummary) => {
    // Reset the form to EMPTY immediately so any half-typed «new user» state
    // doesn't bleed into the edit. The real values come from detailQuery.
    setForm(EMPTY);
    setEditingId(u.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  // Title pulled straight from the list so it doesn't flicker through the
  // prefill window.
  const editingUserFromList = editing
    ? users.find((u) => u.id === editingId)
    : undefined;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Пользователи</h1>
        <p className="text-sm text-muted-foreground">
          Создавайте складских сотрудников и выдавайте им доступ к разделам.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {editing
              ? `Редактирование: ${editingUserFromList?.username ?? "…"}`
              : "Новый пользователь"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <fieldset disabled={formLocked} className="space-y-3">
              {prefilling && (
                <p className="text-xs text-muted-foreground">Загрузка данных пользователя…</p>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="username">Логин</Label>
                  <Input
                    id="username"
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    disabled={editing}
                    required={!editing}
                    minLength={editing ? undefined : 3}
                    pattern={editing ? undefined : "[a-zA-Z0-9_.\\-]+"}
                    title="Латиница, цифры, _.-"
                  />
                </div>
                {!isSelfEdit && (
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Пароль{" "}
                      {editing && (
                        <span className="text-xs text-muted-foreground">
                          (оставьте пустым чтобы не менять)
                        </span>
                      )}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required={!editing}
                      minLength={form.password ? 8 : 0}
                    />
                  </div>
                )}
                {isSelfEdit && (
                  <div className="space-y-2">
                    <Label>Свой пароль</Label>
                    <p className="text-xs text-muted-foreground">
                      Меняется отдельно — раздел Профиль (чтобы текущая сессия не
                      разорвалась).
                    </p>
                  </div>
                )}
              </div>

              {ssoManaged && (
                <p className="text-xs text-muted-foreground" data-testid="sso-managed-note">
                  Права управляются в SSO (Zitadel)
                </p>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={form.is_admin}
                  onChange={(e) => onToggleAdmin(e.target.checked)}
                  disabled={isSelfEdit || ssoManaged}
                  title={
                    isSelfEdit
                      ? "Себе менять права нельзя"
                      : ssoManaged
                        ? "Права управляются в SSO (Zitadel)"
                        : undefined
                  }
                />
                Администратор (видит всё, может создавать пользователей)
                {isSelfEdit && (
                  <span className="text-xs text-muted-foreground">— это вы</span>
                )}
              </label>

              {editing && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    disabled={isSelfEdit}
                    title={
                      isSelfEdit ? "Себя деактивировать нельзя" : undefined
                    }
                  />
                  Активен
                </label>
              )}

              {!form.is_admin && (
                <div className="space-y-1">
                  <div className="text-sm font-medium">Доступные разделы</div>
                  {pages.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Нет доступных разделов.
                    </p>
                  )}
                  <div className="grid gap-1">
                    {pages.map((p) => (
                      <React.Fragment key={p.name}>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="size-4"
                            checked={form.permissions.includes(p.name)}
                            onChange={() => togglePermission(p.name)}
                            disabled={ssoManaged}
                          />
                          {p.title}
                        </label>
                        {/* Точки сборки задаются В ТОЧКЕ (одна форма на одну
                            таблицу) — отсюда только дорога туда. */}
                        {p.name === "fbs" && hasPage("fbs") && (
                          <p className="pl-6 text-xs text-muted-foreground">
                            Точки сборки назначаются в{" "}
                            <Link className="underline" to="/settings/fbs/wb">
                              Настройки → ФБС → Точки сборки
                            </Link>
                          </p>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {editing && (
                <div className="space-y-1">
                  <Label htmlFor="one_c_user">
                    Пользователь 1С{" "}
                    <span className="text-xs text-muted-foreground">
                      (автор вложений в заказ — палетный лист, фото)
                    </span>
                  </Label>
                  {oneCUsersQuery.isLoading && (
                    <p className="text-xs text-muted-foreground">Загрузка пользователей 1С…</p>
                  )}
                  {oneCUsersQuery.isError && (
                    <p className="text-xs text-destructive">
                      Не удалось загрузить пользователей 1С:{" "}
                      {extractErrorMessage(oneCUsersQuery.error)}
                    </p>
                  )}
                  <select
                    id="one_c_user"
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={form.one_c_user_ref}
                    onChange={(e) => {
                      const ref = e.target.value;
                      const name =
                        oneCUsersQuery.data?.find((u) => u.ref === ref)?.name ?? "";
                      setForm({ ...form, one_c_user_ref: ref, one_c_user_name: name });
                    }}
                  >
                    <option value="">— не задано —</option>
                    {/* Текущая привязка может отсутствовать в списке (недействителен) —
                        показываем её отдельно, чтобы не потерять при сохранении. */}
                    {form.one_c_user_ref &&
                      !oneCUsersQuery.data?.some((u) => u.ref === form.one_c_user_ref) && (
                        <option value={form.one_c_user_ref}>
                          {form.one_c_user_name || form.one_c_user_ref} (текущий)
                        </option>
                      )}
                    {(oneCUsersQuery.data ?? []).map((u) => (
                      <option key={u.ref} value={u.ref}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </fieldset>

            <div className="flex gap-2">
              <Button type="submit" disabled={formLocked}>
                {editing ? (
                  "Сохранить"
                ) : (
                  <>
                    <Plus className="size-4" /> Создать
                  </>
                )}
              </Button>
              {editing && (
                <Button type="button" variant="ghost" onClick={cancelEdit}>
                  Отмена
                </Button>
              )}
            </div>
          </form>

          {/* Пропуск — только у существующего пользователя: выдавать код
              несозданному человеку некому. */}
          {editing && (
            <div className="mt-4">
              <BadgeCard
                userId={editingId!}
                username={detailQuery.data?.username ?? form.username}
                issuedAt={detailQuery.data?.badge_issued_at}
                onChanged={() => void detailQuery.refetch()}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Список</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        <ul className="space-y-2">
          {users.map((u) => (
            <li
              key={u.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  {u.username}
                  {u.is_admin && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      админ
                    </span>
                  )}
                  {u.sso_managed && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      SSO
                    </span>
                  )}
                  {!u.is_active && (
                    <span className="rounded bg-destructive/20 px-1.5 py-0.5 text-xs text-destructive">
                      деактивирован
                    </span>
                  )}
                  {me?.id === u.id && (
                    <span className="text-xs text-muted-foreground">это вы</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  1С: {u.one_c_user_name ? (
                    <span className="font-medium text-foreground">{u.one_c_user_name}</span>
                  ) : (
                    <span className="italic">не привязан</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  Создан {new Date(u.created_at).toLocaleString("ru-RU")}
                  {u.last_login_at && (
                    <> · последний вход {new Date(u.last_login_at).toLocaleString("ru-RU")}</>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Редактировать ${u.username}`}
                  onClick={() => startEdit(u)}
                >
                  <Pencil className="size-4" />
                </Button>
                {me && u.id !== me.id && u.is_active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Деактивировать ${u.username}`}
                    onClick={() => {
                      if (window.confirm(`Деактивировать «${u.username}»?`)) {
                        deleteMut.mutate(u.id);
                      }
                    }}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
